import { useCallback } from "react"
import { DESC } from "helpers/sorts"
import { useFactorViewDashboardMetrics } from "helpers/metrics/hooks"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import { CONTENT_SET_DATA_TYPES } from "helpers/contentSetConstants"
import { useAnalytics } from "connected-components/analytics"
import { useIntradayState } from "connected-components/dashboard/hooks/use-intraday-state"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useFactorsViewTableHandlers } from "../../hooks/use-factors-view-table-handlers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useFactorsViewData } from "connected-components/factors-view/hooks/use-factors-view-data"
import { useFactorsViewTemplateChart } from "../../hooks/use-factors-view-template-chart"
import { FACTORS_VIEW_CONTENT_SET_TEMPLATE_KEY, FACTORS_VIEW_CONTENT_SET_DEFAULTS } from "../template-chart-config"
import { FACTORS_VIEW_COMMON_PROPS } from "../../prop-types"

export default function FactorsContentSetView({ instrumentsWithMetadata, instrumentColumns, selectedContentSetMetadata }) {
  const contextData = useFactorsViewData()
  const { sortMetricId, setSortMetricId, sortDirection, setSortDirection, selectedTableLevels, setSelectedTableLevels, view: contentSetId, isEndVsStartDateComparison, startDate, endDate, instrument, comparisonInstrument, secondInstrument, base, filter, normalization, modelId, intradayBatchMetadata, intradayPricingAsOf } = contextData
  const { selectedCategory } = useFactorsViewTemplateChart(
    FACTORS_VIEW_CONTENT_SET_TEMPLATE_KEY,
    FACTORS_VIEW_CONTENT_SET_DEFAULTS,
  )
  const [{ isIntradayMode }] = useIntradayState()

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useFactorsViewTableHandlers({ track: analytics.track })
  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const handleDrilldownRowClick = useCallback((factorId, clickPosition) => {
    if (!panelId || !contentSetId) return
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "factors.content-set",
      title: "Asset View: Content Set",
      view: contentSetId,
      context: {
        modelId,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate,
        isIntradayMode,
        ...resolveIntradayDrilldownFields(isIntradayMode, intradayBatchMetadata, intradayPricingAsOf),
        filter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedFactor: factorId,
        selectedCategory,
      },
    }))
  }, [panelId, openDrilldown, contentSetId, selectedCategory, modelId, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, filter, normalization, isEndVsStartDateComparison])

  const gatherers = useTemplateGatherers(contextData, "factorContentSet", { contentSetId, useCompositionQueries: false, selectedCategory })

  const metrics = useFactorViewDashboardMetrics(
    [{ category: "factorContentSet", isSensitivity: selectedContentSetMetadata.dataType === CONTENT_SET_DATA_TYPES.SENSITIVITY }],
    { contentSetId, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      groupByName: "Category",
      defaultSort: {
        path: `contentSetNetContribution${defaultSortSuffix}`,
        direction: DESC,
      },
      disableCategories: true,
      classes: "factors-table",
      idLabel: "Factor",
      hidePagination: true,
      includeDrilldownArrows: true,
      downloadKey: "factors.contentSet",
      downloadName: "Factors - Content Set",
      sortMetricId,
      onSortMetricIdChange: setSortMetricId,
      sortDirection,
      onSortDirectionChange: setSortDirection,
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      onDrilldownRowClick: handleDrilldownRowClick,
      viewLabel: selectedContentSetMetadata?.name,
    }}
  />
}

FactorsContentSetView.propTypes = FACTORS_VIEW_COMMON_PROPS
